import type {
  PtoEquipment,
  PtoLoadChartPoint,
  PtoPprDocKind,
  PtoPprEquipment,
  PtoPprEquipmentInput,
  PtoPprFact,
  PtoPprFinding,
  PtoPprReview,
  PtoPprStage,
  PtoPprTkDraft,
  PtoPprTkSection,
  PtoPprVolumeRow,
} from '@shared/api';
import { fmtDate, fmtNum } from '../kfo/ui';
import { errorText as apiErrorText, httpStatus } from '../rdImport/errors';

// ППР по РД и технике (режим rd, docs/pto-ppr-rd-spec.md): подписи и чистая
// логика экрана — без React, чтобы правила статусов и проверки мастера были в
// одном месте.

// ─────────── Вид документа ───────────

/** Виды документа режима rd: код, сокращение, полная подпись — в порядке показа. */
export const DOC_KINDS: ReadonlyArray<readonly [PtoPprDocKind, string, string]> = [
  ['ppr', 'ППР', 'ППР — проект производства работ'],
  ['tk', 'ТК', 'ТК — технологическая карта'],
  ['ppr_height', 'ППР на высоте', 'ППР на высоте — проект производства работ на высоте'],
  ['ppr_lifting', 'ППР ПС', 'ППР ПС — проект производства работ подъёмными сооружениями'],
];

/**
 * Виды библиотеки прецедентов и черновиков из прецедентов (mode=precedent) —
 * только ППР и ТК: ППР на высоте и ППР ПС составляются только по РД (ИИ).
 */
export const PRECEDENT_DOC_KINDS = DOC_KINDS.filter(([code]) => code === 'ppr' || code === 'tk');

/** Строка `DOC_KINDS` по коду; неизвестный вид — ППР (как на сервере). */
const docKindEntry = (kind: string | null | undefined) =>
  DOC_KINDS.find(([code]) => code === kind) ?? DOC_KINDS[0];

/** Код вида документа из значения списка; неизвестное — 'ppr'. */
export function docKindOf(kind: string | null | undefined): PtoPprDocKind {
  return docKindEntry(kind)[0];
}

/** Сокращение вида документа: «ППР» | «ТК» | «ППР на высоте» | «ППР ПС». */
export function docShort(kind: string | null | undefined): string {
  return docKindEntry(kind)[1];
}

// ─────────── Техника ───────────

/** Виды техники (§3) в порядке показа в выпадающем списке. */
export const EQUIPMENT_KINDS: ReadonlyArray<readonly [string, string]> = [
  ['crane_truck', 'Автомобильный кран'],
  ['crane_crawler', 'Гусеничный кран'],
  ['crane_tower', 'Башенный кран'],
  ['pipelayer', 'Трубоукладчик'],
  ['crane_manipulator', 'Кран-манипулятор (КМУ)'],
  ['pile_driver', 'Копёр / сваебойная установка'],
  ['vibro_driver', 'Вибропогружатель'],
  ['drilling_rig', 'Буровая установка'],
  ['excavator', 'Экскаватор'],
  ['bulldozer', 'Бульдозер'],
  ['loader', 'Погрузчик'],
  ['roller', 'Каток'],
  ['dump_truck', 'Самосвал'],
  ['flatbed_truck', 'Бортовой автомобиль'],
  ['trailer_tractor', 'Тягач с тралом'],
  ['concrete_mixer', 'Автобетоносмеситель'],
  ['concrete_pump', 'Автобетононасос'],
  ['welding', 'Сварочный агрегат'],
  ['power_plant', 'Дизельная электростанция'],
  ['compressor', 'Компрессор'],
  ['water_tanker', 'Водовоз / автоцистерна'],
  ['other', 'Прочее'],
];

const EQUIPMENT_KIND_LABEL: Record<string, string> = Object.fromEntries(EQUIPMENT_KINDS);

export function equipmentKindLabel(kind: string): string {
  return EQUIPMENT_KIND_LABEL[kind] ?? kind;
}

/** Грузоподъёмные виды: у них г/п, вылет, высота и ГВХ (проверка крана, §6.3). */
const LIFTING_KINDS = new Set([
  'crane_truck',
  'crane_crawler',
  'crane_tower',
  'pipelayer',
  'crane_manipulator',
  'pile_driver',
]);

export function isLiftingKind(kind: string): boolean {
  return LIFTING_KINDS.has(kind);
}

export const MOBILIZATION_METHODS = ['своим ходом', 'на трале', 'ж/д транспортом'];

// ─────────── Справочные данные ИИ (загрузка техники файлом, спека §11) ───────────

/** Параметры техники, которые может дописать ИИ. ГВХ ИИ не заполняет никогда. */
export type EquipmentAiField =
  'kind' | 'characteristics' | 'lift_capacity_t' | 'max_reach_m' | 'lift_height_m';

/** Числовые параметры крана (ключи `ai_params` — те же; в текст характеристики не входят). */
export type EquipmentNumField = 'lift_capacity_t' | 'max_reach_m' | 'lift_height_m';

export const AI_NUMERIC_FIELDS: ReadonlyArray<EquipmentNumField> = [
  'lift_capacity_t',
  'max_reach_m',
  'lift_height_m',
];

/** Подсказка к значку «ИИ», когда подробностей проверки нет. */
export const AI_HINT = 'Справочные данные ИИ — сверить с паспортом';

/** Поля с пометкой ИИ в порядке показа и их подписи. */
const AI_FIELD_LABEL: ReadonlyArray<readonly [EquipmentAiField, string]> = [
  ['kind', 'вид'],
  ['characteristics', 'характеристика'],
  ['lift_capacity_t', 'г/п'],
  ['max_reach_m', 'вылет'],
  ['lift_height_m', 'высота подъёма'],
];

/** «г/п, вылет» — какие параметры из справочных данных ИИ. */
export function aiFieldsText(fields: readonly string[]): string {
  return AI_FIELD_LABEL.filter(([f]) => fields.includes(f))
    .map(([, label]) => label)
    .join(', ');
}

/** «Справочные данные ИИ — сверить с паспортом: г/п, вылет». */
export function aiDetails(fields: readonly string[]): string {
  const list = aiFieldsText(fields);
  return list ? `${AI_HINT}: ${list}` : AI_HINT;
}

/** «Вылет: справочные данные ИИ — сверить с паспортом» — подсказка к значку у одного поля. */
export function aiFieldDetails(field: EquipmentAiField): string {
  const label = aiFieldsText([field]);
  const head = label.charAt(0).toUpperCase() + label.slice(1);
  return `${head}: справочные данные ИИ — сверить с паспортом`;
}

/** Правка поля инженером снимает с него пометку ИИ: значение теперь его. */
export function withoutAi(fields: readonly string[], field: EquipmentAiField): string[] {
  return fields.filter((f) => f !== field);
}

/** Пометка ИИ в тексте характеристики, которую ставит сервер: «…; справочно (ИИ): …». */
const AI_TEXT_MARK = /справочно\s*\(\s*ИИ\s*\)/i;

/**
 * Текст характеристики без метки сервера «справочно (ИИ):» — значения сверены
 * с паспортом: «…; справочно (ИИ): …» → «…; …», ведущая метка убирается.
 */
export function stripAiMark(text: string): string {
  return text
    .replace(/^\s*справочно\s*\(\s*ИИ\s*\)\s*:?\s*/i, '')
    .replace(/\s*;\s*справочно\s*\(\s*ИИ\s*\)\s*:?\s*/gi, '; ')
    .replace(/\s*справочно\s*\(\s*ИИ\s*\)\s*:?\s*/gi, ' ')
    .trim();
}

/**
 * «ИИ ×» — значение сверено с паспортом: пометка снимается, значение остаётся;
 * у характеристики из текста убирается и метка «справочно (ИИ):».
 */
export function verifiedPatch<T extends { ai_fields: string[]; characteristics: string }>(
  row: T,
  field: EquipmentAiField,
): Partial<T> {
  const patch: Partial<T> = {};
  patch.ai_fields = withoutAi(row.ai_fields, field);
  if (field === 'characteristics') patch.characteristics = stripAiMark(row.characteristics);
  return patch;
}

/** Текст в одну строку: переносы и табуляции из ячейки Excel — пробелом (поля однострочные). */
export const oneLine = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** Как сервер сравнивает наименования справочника: без регистра и лишних пробелов. */
export const normName = (s: string): string => s.trim().replace(/\s+/g, ' ').toLowerCase();

/** Число как его пишет сервер (до 3 знаков, запятая) — с точкой или пробелами-разрядами тоже. */
function aiNumberRe(v: number): RegExp {
  const [int, frac = ''] = v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '').split('.');
  const intRe = int.replace(/\B(?=(\d{3})+$)/g, '[\\s\\u00a0\\u202f]?');
  return new RegExp(`(?<![\\d.,])${intRe}${frac ? `[.,]${frac}` : ''}(?![.,]?\\d)`);
}

/**
 * Остались ли в тексте характеристики справочные данные ИИ: числа из
 * `ai_params` (если они известны) или пометка сервера «справочно (ИИ)». Пока
 * остались — правка текста пометку «ИИ» не снимает.
 */
export function aiTextRemains(text: string, values: readonly number[]): boolean {
  if (values.length > 0) return values.some((v) => aiNumberRe(v).test(text));
  return AI_TEXT_MARK.test(text);
}

/** Ошибка запроса справочника техники и загрузки файлом — без служебных префиксов сервера. */
export function equipmentErrorText(err: unknown, fallback: string): string {
  const status = httpStatus(err);
  if (status === 413) return 'Файл больше 10 МБ — сервер такие не принимает.';
  if (status === 403) return 'Нет права записи в разделе ПТО — обратитесь к администратору.';
  return apiErrorText(err, fallback);
}

/** Ошибка загрузки файла техники: у неё свои лимит (429) и очередь разбора (503). */
export function importErrorText(err: unknown, fallback: string): string {
  const status = httpStatus(err);
  if (status === 429) return 'Лимит загрузок файлов техники — 30 в час; повторите позже.';
  if (status === 503) {
    return 'Сервер занят разбором других файлов техники — повторите через пару минут.';
  }
  return equipmentErrorText(err, fallback);
}

/** Код ошибки бэкенда (`{error: {code, message}}`). */
function errorCode(err: unknown): string | null {
  const data = (err as { data?: unknown } | null | undefined)?.data as
    { error?: { code?: unknown } | string } | null | undefined;
  const code = data && typeof data.error === 'object' ? data.error?.code : null;
  return typeof code === 'string' ? code : null;
}

/**
 * Запись справочника изменилась после версии, которую правил инженер (409 с
 * кодом `stale_version`): правку не сохранить — запись открывают заново.
 * Обычный 409 (`conflict`, наименование занято) — не он.
 */
export function isStaleVersion(err: unknown): boolean {
  return httpStatus(err) === 409 && errorCode(err) === 'stale_version';
}

/** Причина, по которой запись не легла в справочник (`results[].error` загрузки списком). */
export function bulkErrorText(message: string | null | undefined): string {
  const m = (message ?? '').trim();
  if (!m) return 'Не сохранено.';
  // Внутренние подробности (SQL, сериализация) инженеру ничего не скажут.
  if (/^(database|internal) error\b/i.test(m)) {
    return 'Не сохранено: внутренняя ошибка сервера — повторите позже или сообщите администратору.';
  }
  // Тот же разбор, что у ответа с ошибкой: служебный префикс «validation: …» снимаем.
  return `Не сохранено: ${apiErrorText({ data: { error: { message: m } } }, m)}`;
}

/** «Автомобильный кран · г/п 25 т · вылет 30 м · ГВХ 12 точек · …» — характеристики техники. */
export function equipmentSummary(
  e: Pick<
    PtoEquipment,
    'kind' | 'characteristics' | 'lift_capacity_t' | 'max_reach_m' | 'lift_height_m' | 'load_chart'
  >,
): string {
  const parts = [equipmentKindLabel(e.kind)];
  if (e.lift_capacity_t != null) parts.push(`г/п ${fmtNum(e.lift_capacity_t)} т`);
  if (e.max_reach_m != null) parts.push(`вылет ${fmtNum(e.max_reach_m)} м`);
  if (e.lift_height_m != null) parts.push(`высота ${fmtNum(e.lift_height_m)} м`);
  if (e.load_chart.length > 0) {
    parts.push(`ГВХ ${e.load_chart.length} ${pointsWord(e.load_chart.length)}`);
  }
  if (e.characteristics) parts.push(e.characteristics);
  return parts.join(' · ');
}

export function pointsWord(n: number): string {
  return plural(n, ['точка', 'точки', 'точек']);
}

// ─────────── Ввод чисел ───────────

/**
 * Число из поля: пусто → null, запятая — десятичный разделитель, пробелы-разряды
 * игнорируются. Не положительное число → NaN (ошибка ввода).
 */
export function parseDecimal(s: string): number | null {
  const t = s.replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(t)) return NaN;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : NaN;
}

/** Строка для поля ввода из числа (запятая, без разрядов). */
export function numToInput(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v).replace('.', ',');
}

// ─────────── ГВХ ───────────

/** Точка ГВХ в редакторе — строками, пока пользователь вводит. */
export interface ChartPointDraft {
  reach: string;
  cap: string;
}

export function chartToDraft(points: PtoLoadChartPoint[]): ChartPointDraft[] {
  return points.map((p) => ({ reach: numToInput(p.reach_m), cap: numToInput(p.capacity_t) }));
}

/** ГВХ из редактора: пустые пары отбрасываются, точки — по возрастанию вылета. */
export function chartFromDraft(points: ChartPointDraft[]): {
  points: PtoLoadChartPoint[];
  error: string | null;
} {
  const out: PtoLoadChartPoint[] = [];
  for (const p of points) {
    if (!p.reach.trim() && !p.cap.trim()) continue;
    const reach = parseDecimal(p.reach);
    const cap = parseDecimal(p.cap);
    if (reach === null || cap === null || Number.isNaN(reach) || Number.isNaN(cap)) {
      return { points: [], error: 'ГВХ: в каждой паре нужны вылет и г/п — положительные числа' };
    }
    out.push({ reach_m: reach, capacity_t: cap });
  }
  out.sort((a, b) => a.reach_m - b.reach_m);
  return { points: out, error: null };
}

/**
 * Пары ГВХ из вставленного текста (столбцы из Excel, «вылет г/п» построчно):
 * разделитель — табуляция, «;» или пробел; запятая — десятичная.
 */
export function chartFromPaste(text: string): ChartPointDraft[] {
  const out: ChartPointDraft[] = [];
  for (const line of text.split(/\r?\n/)) {
    const parts = line
      .trim()
      .split(/[\t; ]+/)
      .filter(Boolean);
    if (parts.length >= 2) out.push({ reach: parts[0], cap: parts[1] });
  }
  return out;
}

// ─────────── Строка техники в мастере ───────────

/** Строка «Графика мобилизации техники» в мастере — строками, пока идёт ввод. */
export interface EqRowDraft {
  key: string;
  equipment_id: string | null;
  name: string;
  kind: string;
  characteristics: string;
  lift_capacity_t: string;
  max_reach_m: string;
  lift_height_m: string;
  load_chart: ChartPointDraft[];
  count: string;
  mobilization_date: string;
  mobilization_method: string;
  purpose: string;
  /** Поля из справочных данных ИИ (`EquipmentAiField`) — значок «ИИ» в ячейке. */
  ai_fields: string[];
  /**
   * Числа справочных данных ИИ в тексте характеристики (`ai_params` загрузки
   * файлом): пока хоть одно осталось в тексте, правка пометку «ИИ» не снимает.
   * На сервер не отправляется.
   */
  ai_values?: number[];
}

let rowSeq = 0;

export function emptyEqRow(): EqRowDraft {
  rowSeq += 1;
  return {
    key: `eq-row-${rowSeq}`,
    equipment_id: null,
    name: '',
    kind: 'other',
    characteristics: '',
    lift_capacity_t: '',
    max_reach_m: '',
    lift_height_m: '',
    load_chart: [],
    count: '1',
    mobilization_date: '',
    mobilization_method: '',
    purpose: '',
    ai_fields: [],
  };
}

/**
 * Выбор из справочника: подставляем вид, характеристику, г/п, вылет, высоту и
 * ГВХ — вместе с пометками ИИ записи.
 */
export function fillFromRegistry(row: EqRowDraft, e: PtoEquipment): EqRowDraft {
  return {
    ...row,
    equipment_id: e.id,
    name: e.name,
    kind: e.kind,
    characteristics: e.characteristics ?? '',
    lift_capacity_t: numToInput(e.lift_capacity_t),
    max_reach_m: numToInput(e.max_reach_m),
    lift_height_m: numToInput(e.lift_height_m),
    load_chart: chartToDraft(e.load_chart),
    ai_fields: [...(e.ai_fields ?? [])],
    ai_values: [],
  };
}

/**
 * Правка параметра в строке мастера: новое значение и снятая с поля пометка ИИ.
 * Характеристика сохраняет пометку, пока в тексте остались справочные данные ИИ.
 */
export function editEqField(
  row: EqRowDraft,
  field: EquipmentAiField,
  value: string,
): Partial<EqRowDraft> {
  const keep = field === 'characteristics' && aiTextRemains(value, row.ai_values ?? []);
  const patch: Partial<EqRowDraft> = {
    ai_fields: keep ? row.ai_fields : withoutAi(row.ai_fields, field),
  };
  patch[field] = value;
  return patch;
}

/** Строки из файла — в конец таблицы; пустые строки-заготовки в конце убираем. */
export function appendEqRows(rows: EqRowDraft[], added: EqRowDraft[]): EqRowDraft[] {
  let end = rows.length;
  while (end > 0 && isBlankEqRow(rows[end - 1])) end -= 1;
  return [...rows.slice(0, end), ...added];
}

/** Есть ли у строки грузоподъёмные параметры (показываем поля даже у «неподъёмного» вида). */
export function rowHasLifting(row: EqRowDraft): boolean {
  return (
    isLiftingKind(row.kind) ||
    !!row.lift_capacity_t.trim() ||
    !!row.max_reach_m.trim() ||
    !!row.lift_height_m.trim() ||
    row.load_chart.length > 0
  );
}

/** Строка, в которую ничего не ввели (вид и кол-во по умолчанию не считаются). */
export function isBlankEqRow(row: EqRowDraft): boolean {
  return (
    !row.name.trim() &&
    !row.characteristics.trim() &&
    !row.lift_capacity_t.trim() &&
    !row.max_reach_m.trim() &&
    !row.lift_height_m.trim() &&
    row.load_chart.every((p) => !p.reach.trim() && !p.cap.trim()) &&
    !row.mobilization_date &&
    !row.mobilization_method.trim() &&
    !row.purpose.trim()
  );
}

export const MAX_EQUIPMENT_ROWS = 60;
export const MAX_REVISIONS = 30;

// Пределы полей техники — как на сервере: иначе строку не сохранить.
export const EQ_NAME_MAX = 200;
export const EQ_CHARACTERISTICS_MAX = 500;
export const EQ_NOTES_MAX = 2000;
const EQ_METHOD_MAX = 100;
const EQ_PURPOSE_MAX = 300;
const EQ_COUNT_MAX = 999;

const EQ_NUM_LIMIT: Record<EquipmentNumField, { label: string; max: number; unit: string }> = {
  lift_capacity_t: { label: 'г/п', max: 10_000, unit: 'т' },
  max_reach_m: { label: 'вылет', max: 500, unit: 'м' },
  lift_height_m: { label: 'высота подъёма', max: 500, unit: 'м' },
};

/** Длина текста в символах (как считает сервер), без крайних пробелов. */
export const textLength = (s: string): number => Array.from(s.trim()).length;

/** Количество техники: целое от 1 до 999. */
export function isEqCount(s: string): boolean {
  const t = s.trim();
  return /^\d+$/.test(t) && Number(t) >= 1 && Number(t) <= EQ_COUNT_MAX;
}

export const EQ_COUNT_ERROR = `количество — целое число от 1 до ${EQ_COUNT_MAX}`;

/** Ошибка числового параметра («вылет — не больше 500 м») или null; пусто — не ошибка. */
export function eqNumError(field: EquipmentNumField, raw: string): string | null {
  const { label, max, unit } = EQ_NUM_LIMIT[field];
  const v = parseDecimal(raw);
  if (v === null) return null;
  if (Number.isNaN(v)) return `${label} — положительное число`;
  return v > max ? `${label} — не больше ${fmtNum(max)} ${unit}` : null;
}

/** Ошибки наименования, характеристики, г/п, вылета и высоты — общие для мастера и загрузки файлом. */
export function eqFieldErrors(
  v: Pick<EqRowDraft, 'name' | 'characteristics' | EquipmentNumField>,
): string[] {
  const errors: string[] = [];
  if (!v.name.trim()) errors.push('укажите наименование и марку');
  else if (textLength(v.name) > EQ_NAME_MAX) {
    errors.push(`наименование — не длиннее ${EQ_NAME_MAX} символов`);
  }
  if (textLength(v.characteristics) > EQ_CHARACTERISTICS_MAX) {
    errors.push(`техническая характеристика — не длиннее ${EQ_CHARACTERISTICS_MAX} символов`);
  }
  for (const f of AI_NUMERIC_FIELDS) {
    const e = eqNumError(f, v[f]);
    if (e) errors.push(e);
  }
  return errors;
}

/** Ошибки таблицы техники — по строкам, с номером строки как в таблице. */
export function validateEqRows(rows: EqRowDraft[]): string[] {
  const errors: string[] = [];
  const filled = rows.filter((r) => !isBlankEqRow(r));
  if (filled.length > MAX_EQUIPMENT_ROWS) {
    errors.push(`Не больше ${MAX_EQUIPMENT_ROWS} строк техники.`);
  }
  rows.forEach((r, i) => {
    if (isBlankEqRow(r)) return;
    const n = `Строка ${i + 1}`;
    const rowErrors = eqFieldErrors(r);
    if (!isEqCount(r.count)) rowErrors.push(EQ_COUNT_ERROR);
    if (textLength(r.mobilization_method) > EQ_METHOD_MAX) {
      rowErrors.push(`способ мобилизации — не длиннее ${EQ_METHOD_MAX} символов`);
    }
    if (textLength(r.purpose) > EQ_PURPOSE_MAX) {
      rowErrors.push(`назначение — не длиннее ${EQ_PURPOSE_MAX} символов`);
    }
    const chart = chartFromDraft(r.load_chart);
    if (chart.error) rowErrors.push(chart.error);
    for (const e of rowErrors) errors.push(`${n}: ${e}.`);
  });
  return errors;
}

/** Обрезка текста до предела, как на сервере: последний символ — «…». */
export function clipText(s: string, max: number): string {
  const chars = Array.from(s);
  if (chars.length <= max) return s;
  return `${chars
    .slice(0, max - 1)
    .join('')
    .trimEnd()}…`;
}

const squash = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();

/** Хвостовые «.», «;», «,» и пробелы — сервер срезает их у текста файла перед «; справочно (ИИ):». */
const TRAILING_PUNCT = /[.;,\s]+$/;

/** Текст файла для сравнения: без регистра, лишних пробелов и хвостовой пунктуации. */
const noteKey = (s: string) => squash(s).replace(TRAILING_PUNCT, '');

/** Текст файла уже есть в характеристике (без учёта регистра, пробелов и хвостовой пунктуации). */
export function hasFileNote(characteristics: string, note: string): boolean {
  const n = noteKey(note);
  return n === '' || squash(characteristics).includes(n);
}

/**
 * Текст колонки «Характеристики / Примечание» файла — в характеристику: если он
 * уже там, ничего не меняем; если характеристика — его обрезок, заменяем;
 * иначе ставим текст файла перед ней (как собирает сервер). Не длиннее 500.
 */
export function mergeFileNote(characteristics: string, note: string): string {
  const n = oneLine(note);
  const c = characteristics.trim();
  if (hasFileNote(c, n)) return c;
  const stem = squash(c).replace(/…$/, '').replace(TRAILING_PUNCT, '');
  if (!stem || noteKey(n).includes(stem)) return clipText(n, EQ_CHARACTERISTICS_MAX);
  return clipText(`${n.replace(TRAILING_PUNCT, '')}; ${c}`, EQ_CHARACTERISTICS_MAX);
}

const orNull = (s: string): string | null => s.trim() || null;

/** Строка мастера → техника запуска генерации (вызывать после `validateEqRows`). */
export function eqRowToInput(r: EqRowDraft): PtoPprEquipmentInput {
  return {
    equipment_id: r.equipment_id,
    name: r.name.trim(),
    kind: r.kind,
    characteristics: orNull(r.characteristics),
    lift_capacity_t: parseDecimal(r.lift_capacity_t),
    max_reach_m: parseDecimal(r.max_reach_m),
    lift_height_m: parseDecimal(r.lift_height_m),
    load_chart: chartFromDraft(r.load_chart).points,
    count: Number(r.count.trim()),
    mobilization_date: r.mobilization_date || null,
    mobilization_method: orNull(r.mobilization_method),
    purpose: orNull(r.purpose),
    // У пустых полей пометку снимает сервер.
    ai_fields: r.ai_fields,
  };
}

// ─────────── Ход генерации и аудит ───────────

/** Черновик rd занят: идёт генерация или цикл аудитора — содержимое не показываем. */
export function isRdBusy(d: PtoPprTkDraft): boolean {
  return (
    d.mode === 'rd' &&
    (d.gen_status === 'queued' || d.gen_status === 'running' || d.review?.status === 'running')
  );
}

/** Хода работы нет дольше — воркер, похоже, упал: бэкенд разрешает перезапуск занятого черновика. */
export const STALL_MS = 30 * 60 * 1000;

export const STALL_TEXT = 'Похоже, генерация зависла (нет хода работы больше 30 мин)';

/** Занятый черновик, у которого `updated_at` старше 30 минут. */
export function isRdStalled(d: PtoPprTkDraft, now: number): boolean {
  if (!isRdBusy(d)) return false;
  const at = Date.parse(d.updated_at);
  return Number.isFinite(at) && now - at > STALL_MS;
}

/** «Пишу разделы 7/20 — 7.6 Технология производства основных работ». */
export function progressLabel(d: PtoPprTkDraft): string {
  if (d.gen_status === 'queued') return 'В очереди';
  const p = d.gen_progress;
  switch (p?.stage) {
    case 'queued':
      return 'В очереди';
    case 'facts':
      return 'Изучаю РД…';
    case 'sections': {
      const count = p.done != null && p.total != null ? ` ${p.done}/${p.total}` : '';
      return `Пишу разделы${count}${p.current ? ` — ${p.current}` : ''}`;
    }
    case 'audit':
      return p.round ? `Аудит, круг ${p.round}` : 'Аудит';
    case 'fix':
      return `Исправляю по замечаниям аудитора${p.round ? ` (круг ${p.round})` : ''}`;
    case 'done':
      return 'Завершаю…';
    default:
      return d.review?.status === 'running' ? 'Аудит…' : 'Запуск…';
  }
}

/** Доля выполненного для полосы прогресса — только там, где сервер считает done/total. */
export function progressPercent(d: PtoPprTkDraft): number | null {
  const p = d.gen_progress;
  if (!p || (p.stage !== 'sections' && p.stage !== 'fix')) return null;
  if (p.done == null || !p.total) return null;
  return Math.round((100 * p.done) / p.total);
}

/** Этапы конвейера для экрана хода работы: «РД → разделы → аудит → исправления → готово». */
export const GEN_STEPS: ReadonlyArray<readonly [PtoPprStage, string]> = [
  ['facts', 'РД'],
  ['sections', 'Разделы'],
  ['audit', 'Аудит'],
  ['fix', 'Исправления'],
  ['done', 'Готово'],
];

/** Индекс текущего этапа в `GEN_STEPS`; −1 — ещё в очереди. */
export function currentStepIndex(stage: PtoPprStage | null | undefined): number {
  if (!stage || stage === 'queued') return -1;
  return GEN_STEPS.findIndex(([k]) => k === stage);
}

/** Блокирующие замечания — critical и major (§6.5). */
/** Замечание с перечнем разделов, к которым оно относится. */
export interface FindingGroup {
  finding: PtoPprFinding;
  sectionKeys: string[];
}

/**
 * Замечание аудитора к нескольким разделам хранится по одному на раздел (их
 * исправляет писатель) — для показа и подсчёта это одно замечание.
 */
export function groupFindings(findings: PtoPprFinding[]): FindingGroup[] {
  const out: FindingGroup[] = [];
  for (const f of findings) {
    const g = out.find(
      (x) =>
        x.finding.severity === f.severity &&
        x.finding.origin === f.origin &&
        x.finding.message === f.message &&
        (x.finding.fix ?? null) === (f.fix ?? null),
    );
    if (!g) {
      out.push({ finding: f, sectionKeys: f.section_key ? [f.section_key] : [] });
    } else if (f.section_key && !g.sectionKeys.includes(f.section_key)) {
      g.sectionKeys.push(f.section_key);
    }
  }
  return out;
}

/** Блокирующие замечания (critical, major) без повторов по разделам. */
export function blockingFindings(review: PtoPprReview | null | undefined): PtoPprFinding[] {
  return groupFindings(review?.findings ?? [])
    .map((g) => g.finding)
    .filter((f) => f.severity === 'critical' || f.severity === 'major');
}

export type Tone = 'neutral' | 'warning' | 'danger' | 'success' | 'info';

/** Итог аудита для списка и шапки черновика; null — без значка. */
export function reviewBadge(review: PtoPprReview | null | undefined): {
  tone: Tone;
  text: string;
} | null {
  switch (review?.status) {
    case 'approved':
      return { tone: 'success', text: 'Утверждён ИИ-аудитором' };
    case 'not_approved': {
      const n = blockingFindings(review).length;
      return {
        tone: 'danger',
        text:
          n > 0
            ? `Не утверждён: ${n} ${plural(n, ['замечание', 'замечания', 'замечаний'])}`
            : 'Не утверждён аудитором',
      };
    }
    case 'stale':
      return { tone: 'warning', text: 'Изменён после утверждения — проверить заново' };
    case 'failed':
      return { tone: 'danger', text: 'Аудит не выполнен' };
    case 'none':
      return { tone: 'neutral', text: 'Аудит не проводился' };
    default:
      return null;
  }
}

export const SEVERITY_ORDER = ['critical', 'major', 'minor'] as const;

export const SEVERITY_LABEL: Record<PtoPprFinding['severity'], string> = {
  critical: 'критично',
  major: 'существенно',
  minor: 'незначительно',
};

export const SEVERITY_TONE: Record<PtoPprFinding['severity'], Tone> = {
  critical: 'danger',
  major: 'warning',
  minor: 'neutral',
};

export const ORIGIN_LABEL: Record<PtoPprFinding['origin'], string> = {
  ai: 'аудитор',
  check: 'проверка кода',
};

// ─────────── Разделы ───────────

/** Значок источника текста раздела. */
export function sourceMeta(source: string): { label: string; tone: Tone } {
  switch (source) {
    case 'llm':
      return { label: 'ИИ', tone: 'warning' };
    case 'data':
      return { label: 'данные', tone: 'info' };
    case 'engineer':
      return { label: 'за инженером', tone: 'neutral' };
    case 'form':
      return { label: 'форма', tone: 'neutral' };
    case 'precedent':
      return { label: 'прецедент', tone: 'success' };
    default:
      return { label: source, tone: 'neutral' };
  }
}

/** ИИ пересобирает только свой текст; данные, заглушки и форма собираются кодом. */
export function canRegenerate(s: PtoPprTkSection): boolean {
  return s.source !== 'engineer' && s.source !== 'form' && s.source !== 'data';
}

/** «7.6. Технология производства основных работ». */
export function sectionHeading(s: Pick<PtoPprTkSection, 'number' | 'title'>): string {
  return s.number ? `${s.number}. ${s.title}` : s.title;
}

export function sectionLabelByKey(d: PtoPprTkDraft, key: string): string {
  const s = d.sections.find((x) => x.key === key);
  return s ? sectionHeading(s) : key;
}

/** id якоря раздела — для перехода из замечания аудитора. */
export function sectionAnchor(draftId: string, key: string): string {
  return `ppr-${draftId}-${key}`;
}

/** Абзацы и списки текста раздела: `\n` — абзац, «- » — пункт списка. */
export type TextBlock = { kind: 'p'; text: string } | { kind: 'ul'; items: string[] };

export function toBlocks(content: string): TextBlock[] {
  const out: TextBlock[] = [];
  for (const raw of content.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('- ')) {
      const item = line.slice(2).trim();
      const last = out[out.length - 1];
      if (last && last.kind === 'ul') last.items.push(item);
      else out.push({ kind: 'ul', items: [item] });
    } else {
      out.push({ kind: 'p', text: line });
    }
  }
  return out;
}

/** Заглушки модели «[уточнить: …]» / «[уточнить норматив]» — подсвечиваются в тексте. */
export const PLACEHOLDER_RE = /(\[уточнить[^\]]*\])/i;

// ─────────── Паспорт РД и ссылки ───────────

export const FACT_KIND_ORDER = [
  'object',
  'structure',
  'element',
  'material',
  'elevation',
  'requirement',
  'geology',
  'condition',
  'test',
  'other',
];

export const FACT_KIND_LABEL: Record<string, string> = {
  object: 'Объект',
  structure: 'Конструктивные решения',
  element: 'Элементы и конструкции',
  material: 'Материалы',
  elevation: 'Отметки',
  requirement: 'Требования РД',
  geology: 'Инженерно-геологические условия',
  condition: 'Условия производства работ',
  test: 'Испытания и контроль',
  other: 'Прочее',
};

export const CALC_KIND_LABEL: Record<string, string> = {
  heaviest: 'Наиболее тяжёлый элемент',
  crane_check: 'Проверка грузоподъёмности',
  no_lifting: 'Грузоподъёмные машины',
  work_scheme: 'Схема производства работ',
  labor: 'Калькуляция затрат труда (ГЭСН)',
  schedule: 'График выполнения работ',
  site_plan: 'Стройгенплан (схема)',
};

export const CALC_STATUS: Record<string, { label: string; tone: Tone }> = {
  ok: { label: 'в норме', tone: 'success' },
  fail: { label: 'не проходит', tone: 'danger' },
  unknown: { label: 'нет данных', tone: 'neutral' },
  partial: { label: 'проверить', tone: 'warning' },
};

/** «КЖ-1, стр. 3, лист 2». */
export function factSource(
  f: Pick<PtoPprFact, 'document_code' | 'page_number' | 'sheet_ref'>,
): string {
  return `${f.document_code}, стр. ${f.page_number}${f.sheet_ref ? `, ${f.sheet_ref}` : ''}`;
}

export function volumeLine(v: PtoPprVolumeRow): string {
  const name = [v.name, v.designation, v.code].filter(Boolean).join(' · ');
  const qty = v.quantity != null ? `: ${fmtNum(v.quantity)}${v.unit ? ` ${v.unit}` : ''}` : '';
  const mass = v.unit_mass_kg != null ? `; масса ед. ${fmtNum(v.unit_mass_kg)} кг` : '';
  return `${name}${qty}${mass}`;
}

export function equipmentLine(e: PtoPprEquipment): string {
  const parts = [`${e.name} — ${e.count} шт.`, equipmentSummary(e)];
  if (e.mobilization_date) parts.push(`мобилизация ${fmtDate(e.mobilization_date)}`);
  if (e.mobilization_method) parts.push(e.mobilization_method);
  if (e.purpose) parts.push(`назначение: ${e.purpose}`);
  if ((e.ai_fields ?? []).length > 0) parts.push(aiDetails(e.ai_fields ?? []));
  return parts.join('\n');
}

/** Вид ссылки по префиксу id: rd | vol | eq | calc | std | in. */
export function citationKind(id: string): string {
  return id.split('-')[0] ?? '';
}

/**
 * Подсказка к чипу ссылки: текст и цитата сведения РД с листом, строка объёма,
 * техника, расчёт, норматив. `eq-N` — N-я строка техники мастера (с 1).
 * Не нашли — подпись чипа.
 */
export function citationTitle(id: string, label: string, d: PtoPprTkDraft): string {
  const facts = d.facts;
  switch (citationKind(id)) {
    case 'rd': {
      const f =
        facts?.items?.find((x) => x.id === id) ?? (facts?.object?.id === id ? facts.object : null);
      if (f) return `${f.text}\n«${f.quote}»\n${factSource(f)}`;
      break;
    }
    case 'vol': {
      const v = facts?.volumes?.find((x) => x.id === id);
      if (v)
        return `${volumeLine(v)}\n${v.document_code}${v.source_ref ? `, ${v.source_ref}` : ''}`;
      break;
    }
    case 'eq': {
      const n = Number(id.slice(3));
      const e = Number.isInteger(n) && n > 0 ? d.inputs?.equipment?.[n - 1] : undefined;
      if (e) return equipmentLine(e);
      break;
    }
    case 'calc': {
      const c = facts?.calcs?.find((x) => x.id === id);
      if (c) {
        const st = CALC_STATUS[c.status]?.label ?? c.status;
        return `${CALC_KIND_LABEL[c.kind] ?? c.kind} (${st})\n${c.text}`;
      }
      break;
    }
    case 'std': {
      const s = facts?.norms?.find((x) => x.id === id);
      if (s) {
        const origin =
          s.origin === 'rd'
            ? `из РД${s.source_label ? `: ${s.source_label}` : ''}`
            : 'базовый перечень';
        return `${s.code} ${s.title}\n${origin}`;
      }
      break;
    }
    case 'in':
      return `${label}\nСведения инженера из мастера`;
  }
  return label;
}

/** Короткая подпись ссылки без готового label (refs замечаний аудитора). */
export function citationShortLabel(id: string, d: PtoPprTkDraft): string {
  const facts = d.facts;
  switch (citationKind(id)) {
    case 'rd': {
      const f =
        facts?.items?.find((x) => x.id === id) ?? (facts?.object?.id === id ? facts.object : null);
      return f ? factSource(f) : id;
    }
    case 'vol':
      return facts?.volumes?.find((x) => x.id === id)?.name ?? id;
    case 'eq': {
      const n = Number(id.slice(3));
      return (Number.isInteger(n) && n > 0 && d.inputs?.equipment?.[n - 1]?.name) || id;
    }
    case 'calc': {
      const c = facts?.calcs?.find((x) => x.id === id);
      return c ? (CALC_KIND_LABEL[c.kind] ?? c.kind) : id;
    }
    case 'std':
      return facts?.norms?.find((x) => x.id === id)?.code ?? id;
    default:
      return id;
  }
}

const REF_ID = '(?:rd|vol|eq|calc|std|in|lib)-\\d+';
/** Перечень меток в квадратных или круглых скобках («[rd-3, vol-1]», «(eq-1)») либо одна метка без скобок. */
const REF_IN_TEXT_RE = new RegExp(
  `[[(]\\s*(${REF_ID}(?:\\s*[,;]\\s*${REF_ID})*)\\s*[\\])]|\\b(${REF_ID})\\b`,
  'g',
);

/**
 * Метки источников в тексте замечания («сваи из rd-29», «[eq-1]», «(calc-2)») —
 * понятными ссылками «(КЖ0, стр. 1)» без двойных скобок; неизвестная метка
 * остаётся как есть.
 */
export function humanizeRefs(text: string, d: PtoPprTkDraft): string {
  return text.replace(REF_IN_TEXT_RE, (m, group?: string, bare?: string) => {
    const ids = (group ?? bare ?? '').split(/[,;]/).map((s) => s.trim());
    const labels = ids.map((id) => citationShortLabel(id, d));
    return labels.every((l, i) => l === ids[i]) ? m : `(${labels.join('; ')})`;
  });
}

// ─────────── Общее ───────────

/** Русское множественное число: plural(5, ['замечание', 'замечания', 'замечаний']). */
export function plural(n: number, [one, few, many]: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
